import type { ThemeConfig } from "@/types/theme";

export const tailwindAmber: ThemeConfig = {
  classes: {
    // Color classes
    primary: "text-amber-700 dark:text-amber-400",
    secondary: "text-amber-900 dark:text-amber-500",
    accent: "text-orange-600 dark:text-orange-400",

    // Background classes
    backgroundPrimary: "bg-white dark:bg-gray-950",
    backgroundSecondary: "bg-gray-50 dark:bg-gray-900",
    backgroundSurface: "bg-gray-50 dark:bg-gray-800",
    backgroundTertiary: "bg-amber-50/60 dark:bg-gray-800",
    backgroundAccent: "bg-amber-600 dark:bg-amber-700",
    backgroundPrimaryHover: "hover:bg-gray-50 dark:hover:bg-gray-900",
    decorativeBackground:
      "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-900 dark:to-gray-950",

    // Shared alternates (same across palettes)
    backgroundSoft: "bg-gray-50 dark:bg-gray-900",
    backgroundLighter: "bg-white dark:bg-gray-900",

    backgroundPattern: "bg-amber-100 dark:bg-gray-800",
    backgroundPrimaryColor: "bg-amber-600 dark:bg-amber-500",
    backgroundSecondaryColor: "bg-amber-800 dark:bg-amber-700",
    backgroundAccentColor: "bg-orange-500 dark:bg-orange-400",
    backgroundPrimarySubtle: "bg-amber-50 dark:bg-amber-950",

    // Card backgrounds
    backgroundCard: "bg-white dark:bg-gray-950",
    backgroundElevated: "bg-white dark:bg-gray-800",
    backgroundHighlighted:
      "bg-gradient-to-br from-white to-amber-50 dark:from-gray-800 dark:to-gray-900",

    // Text classes
    textPrimary: "text-gray-800 dark:text-gray-100",
    textSecondary: "text-gray-600 dark:text-gray-300",
    textMuted: "text-gray-500 dark:text-gray-400",
    textInverse: "text-white dark:text-gray-900",
    textAccent: "text-amber-700 dark:text-amber-400",
    textOnAccent: "text-white",

    // Text on dark backgrounds
    textPrimaryOnDark: "text-gray-100",
    textSecondaryOnDark: "text-gray-300",
    textMutedOnDark: "text-gray-400",

    // Icon classes
    iconText: "text-amber-600 dark:text-amber-400",
    iconBackgroundFilled: "bg-amber-100 dark:bg-amber-950",
    iconBackgroundGradient: "bg-gradient-to-br from-amber-500 to-orange-500",
    iconBackgroundDefault: "bg-amber-50 dark:bg-amber-950/50",
    iconBorder: "border-amber-200 dark:border-amber-800",

    // Border classes
    borderDefault: "border-amber-200 dark:border-gray-700",
    borderLight: "border-amber-100 dark:border-gray-800",
    borderStrong: "border-amber-300 dark:border-gray-600",
    borderPrimary: "border-amber-700 dark:border-amber-500",
    borderSecondary: "border-amber-900 dark:border-amber-700",
    borderBottom: "border-b border-amber-200 dark:border-gray-700",
    borderAccent: "border-amber-600 dark:border-amber-400",

    // Badge classes
    badgeBackground: "bg-amber-100 dark:bg-amber-950",
    badgeText: "text-amber-700 dark:text-amber-300",

    // Shadow classes
    shadowPrimary: "shadow-amber-500/20",
    shadowDefault: "shadow-sm",
    shadowHover: "hover:shadow-lg hover:shadow-amber-500/10",
    shadowSmall: "shadow-sm",
    shadowMedium: "shadow-md",
    shadowLarge: "shadow-lg",
    shadowXLarge: "shadow-xl",
    shadowGlow: "shadow-[0_0_30px_rgba(245,158,11,0.30)]", // amber-500 glow
    shadowInset: "shadow-inner",

    // Interactive states
    hoverBackground: "hover:bg-amber-50 dark:hover:bg-gray-800",
    activeBackground: "active:bg-amber-100 dark:active:bg-gray-700",
    accentPrimary:
      "bg-amber-600 hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600",

    // Creative Bold Hero classes
    backgroundBold: "bg-amber-400 dark:bg-amber-600",
    textEmphasis: "text-violet-600 dark:text-violet-400",

    // Gradient classes
    gradientPrimary: "bg-gradient-to-r from-amber-600 to-amber-700",
    gradientSecondary: "bg-gradient-to-br from-amber-700 to-amber-900",
    gradientAccent: "bg-gradient-to-r from-amber-500 to-orange-500",
    gradientSubtle:
      "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-900 dark:to-gray-950",
    gradientVibrant:
      "bg-gradient-to-r from-amber-600 via-orange-500 to-amber-600",
    gradientHero:
      "bg-gradient-to-br from-amber-600 via-orange-500 to-amber-700",

    // Border radius classes
    roundedSmall: "rounded",
    roundedMedium: "rounded-md",
    roundedLarge: "rounded-lg",
    roundedXLarge: "rounded-xl",
    roundedFull: "rounded-full",

    // Opacity classes
    opacityDisabled: "opacity-50",
    opacityHover: "opacity-80",
    opacityBackdrop: "opacity-60",
    opacitySubtle: "opacity-30",

    // Transition classes
    transitionFast: "transition-all duration-150",
    transitionNormal: "transition-all duration-300",
    transitionSlow: "transition-all duration-500",

    // Z-index classes
    zDropdown: "z-10",
    zModal: "z-50",
    zOverlay: "z-40",
    zTooltip: "z-[60]",
    zFixed: "z-30",

    // Status colors (consistent across palettes)
    error: "text-red-600 dark:text-red-400",
    success: "text-green-600 dark:text-green-400",
    warning: "text-amber-600 dark:text-amber-400",
    info: "text-blue-600 dark:text-blue-400",

    errorBg: "bg-red-100 dark:bg-red-950",
    successBg: "bg-green-100 dark:bg-green-950",
    warningBg: "bg-amber-100 dark:bg-amber-950",
    infoBg: "bg-blue-100 dark:bg-blue-950",
  },
};
